<!--
  ====================
    Version : 0.1.15
  ====================

  Version Changed : 2023-09-15
  
  Last Update Changes : 
  * Added path option to data and meta
  * Removed unmodifiable filter configs
  * Re-arranged the order of props and categorized them
-->
<template>
  <div class="vue_universal_table">
    <div class="row" v-if="!propsValid.valid || config.currentlyFetching">
      <div
        class="col"
        v-if="!propsValid.valid"
        style="padding: 20px; text-align: center; color: crimson"
      >
        {{ propsValid.reason }}
      </div>
    </div>
    <div>
      <div
        class="row mb-3"
        v-if="
          specialButtons.length > 0 ||
          (!config.disablePerPage && !config.infiniteScrollEnabled)
        "
      >
        <div
          class="col"
          :class="{
            specialButtons:
              !mobile &&
              !config.disablePerPage &&
              !config.infiniteScrollEnabled,
            'text-right': !(
              !config.disablePerPage && !config.infiniteScrollEnabled
            ),
          }"
        >
          <div
            v-if="
              !mobile && !config.disablePerPage && !config.infiniteScrollEnabled
            "
          >
            Per Page:&nbsp;
            <select
              class="selectPerPage"
              style="border-top: 0; border-left: 0; border-right: 0"
              v-model="config.limitPerPage"
            >
              <option v-for="(num, index) in config.limitPerPageOptions">
                {{ num }}
              </option>
            </select>
          </div>
          <div v-if="specialButtons.length > 0">
            <button
              v-for="button in specialButtons"
              type="button"
              class="btn btn-primary"
              :class="{ 'w-100 mb-2': mobile, 'ml-2': !mobile }"
              @click="emit(button.variable)"
            >
              {{ button.title }}
            </button>
          </div>
        </div>
      </div>

      <!-- TABLE CONTENT STARTS -->
      <!-- <div class="innerBox"> -->
      <div
        class="vue_universal_table_table scan-history-table-wrapper"
        style="overflow-x: auto; overflow-y: hidden"
      >
        <div
          class="col text-center mt-3 mb-3"
          v-if="
            config.currentlyFetching &&
            (!config.fixedHeader || tableData.length == 0)
          "
        >
          <fold :loading="true"></fold>
        </div>
        <table
          class="table scan-history-table"
          :class="{
            fixedHeader: config.fixedHeader,
            'table-striped': config.tableStriped,
          }"
          style="margin: 0"
        >
          <!-- HEADER START -->
          <thead :style="{ 'background-color': config.theadBackgroundColor }">
            <tr>
              <th class="tableTitle" v-if="config.indexing">No</th>
              <template v-for="title in tableHeaders.displayed">
                <th
                  v-if="config.hide_column.indexOf(title.variable) === -1"
                  class="tableTitle"
                  :class="{ sortable: !title.disableSort }"
                  @click="selectAndSort(title.variable, title.disableSort)"
                >
                  {{ title.title }}
                </th>
              </template>
              <th
                v-if="customButtons.title.length > 0"
                :colspan="customButtons.title.length"
                style="text-align: center"
              >
                Action
              </th>
            </tr>
          </thead>
          <!-- HEADER END -->

          <!-- CONTENT START -->
          <tbody
            :style="
              config.fixedHeader ? 'max-height:' + config.tbodyHeight : ''
            "
          >
            <tr
              v-for="(singleRow, numbering) in tableData"
              :style="processRowStyle(singleRow)"
            >
              <!-- Numbering/Indexing -->
              <td v-if="config.indexing" class="tableContent">
                {{
                  (config.currentPage - 1) * config.limitPerPage + numbering + 1
                }}
              </td>

              <!-- All Data -->
              <template v-for="tableHeader in tableHeaders.displayed">
                <td
                  v-if="config.hide_column.indexOf(tableHeader.variable) === -1"
                  class="tableContent"
                  :class="[
                    tableHeader.class,
                    determineCondition(
                      'class',
                      tableHeader.class_condition,
                      singleRow[tableHeader.variable]
                    ),
                  ]"
                  :style="
                    determineCondition(
                      'style',
                      tableHeader.style_condition,
                      singleRow[tableHeader.variable]
                    )
                  "
                  @click="
                    emit(
                      tableHeader.variable,
                      tableHeader.emitFull
                        ? singleRow
                        : singleRow[tableHeader.variable]
                    )
                  "
                >
                  <!-- CONTENT START -->
                  <span v-if="tableHeader.numberWithCommas">
                    {{ numberWithCommas(singleRow[tableHeader.variable]) }}
                  </span>
                  <span v-else-if="tableHeader.dateFormat">
                    {{
                      moment(singleRow[tableHeader.variable]).format(
                        tableHeader.dateFormat
                      )
                    }}
                  </span>
                  <img
                    v-else-if="tableHeader.isImage"
                    :src="singleRow[tableHeader.variable]"
                    :title="singleRow[tableHeader.variable].split('/').pop()"
                  />
                  <span v-else>
                    {{ singleRow[tableHeader.variable] }}
                  </span>
                  <!-- {{tableHeader.numberWithCommas ? numberWithCommas(singleRow[tableHeader.variable]) : moment(singleRow[tableHeader.variable], 'YYYY-MM-DDTHH:mm:ss.000Z', true).isValid() ? moment(singleRow[tableHeader.variable]).format('YYYY-MM-DD HH:mm:ss') : singleRow[tableHeader.variable]}} -->
                  <!-- CONTENT END -->
                </td>
              </template>

              <!-- Buttons Start -->
              <td
                v-for="(button, index) in customButtons.title"
                style="text-align: center"
              >
                <!-- No Icon Button -->
                <!-- No Icon Button -->
                <template
                  v-if="$eval(singleRow, customButtons.condition[index])"
                >
                  <button
                    v-if="customButtons.FAClass[index] == 0"
                    :title="toCapitalizedWords(button)"
                    type="button"
                    class="btn"
                    :class="
                      customButtons.class[index] != 0
                        ? customButtons.class[index]
                        : 'btn-primary'
                    "
                    :style="
                      customButtons.style[index] != 0
                        ? customButtons.style[index]
                        : ''
                    "
                    @click="
                      emit(
                        button,
                        customButtons.variable[index] != null &&
                          customButtons.variable[index] != ''
                          ? singleRow[customButtons.variable[index]]
                          : singleRow
                      )
                    "
                  >
                    {{ toCapitalizedWords(button) }}
                  </button>

                  <!-- Fontawesome Button -->
                  <button
                    v-else
                    :title="toCapitalizedWords(button)"
                    class="btn"
                    :class="
                      customButtons.class[index] != 0
                        ? customButtons.class[index]
                        : 'btn-primary'
                    "
                    :style="
                      customButtons.style[index] != 0
                        ? customButtons.style[index]
                        : ''
                    "
                    @click="
                      emit(
                        button,
                        customButtons.variable[index] != null &&
                          customButtons.variable[index] != ''
                          ? singleRow[customButtons.variable[index]]
                          : singleRow
                      )
                    "
                  >
                    <i
                      :class="
                        customButtons.FAClass[index] != 0
                          ? customButtons.FAClass[index]
                          : ''
                      "
                    ></i>
                  </button>
                </template>
                <template v-else>
                  <span>-</span>
                </template>
              </td>
              <!-- Buttons End -->
            </tr>
            <tr
              v-if="
                config.infiniteScrollEnabled &&
                (config.currentPage < config.maxPage || config.maxPage == -1)
              "
            >
              <td colspan="100%" v-observe-visibility="loadingSeen">
                <h4
                  class="mt-2"
                  style="text-align: center; width: 100%; margin: 0"
                >
                  Loading...
                </h4>
              </td>
            </tr>
          </tbody>
          <!-- CONTENT END -->
        </table>
      </div>
      <!-- TABLE CONTENT ENDS -->
      <!-- PAGINATION STARTS -->
      <div
        class="row mt-3"
        v-if="config.paginationExist && !config.infiniteScrollEnabled"
      >
        <div class="col-12 d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center flex-wrap">
            <p class="mt-2 mb-2" style="color: #a3a4a6; margin-right: 20px; margin-bottom: 0;">
              Showing {{ (config.currentPage - 1) * config.limitPerPage + 1 }}-{{ config.currentPage * config.limitPerPage < config.total ? config.currentPage * config.limitPerPage : config.total }} of {{ config.total }} entries
            </p>
            <nav class="pagination-minimal">
              <ul class="pagination" style="margin: 0; flex-wrap: wrap;">
                <li class="page-item" :class="{ disabled: config.currentPage <= 1 }" @click.stop.prevent="changePage(1)">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&laquo;</a>
                </li>
                <li class="page-item" :class="{ disabled: config.currentPage <= 1 }" @click.stop.prevent="changePage(5)">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&lsaquo;</a>
                </li>
                <li class="page-item pageNumber" style="cursor: pointer">
                  <a class="page-link" href="javascript:void(0)" style="color: #4B4B4B">
                    <span style="font-weight: bold !important">{{ config.maxPage < 1 ? 0 : config.currentPage }}</span> of {{ config.maxPage }}
                  </a>
                </li>
                <li class="page-item" :class="{ disabled: config.currentPage >= config.maxPage }" @click.stop.prevent="changePage(6)">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&rsaquo;</a>
                </li>
                <li class="page-item" :class="{ disabled: config.currentPage >= config.maxPage }" @click.stop.prevent="changePage(10)">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      <!-- PAGINATION ENDS -->
      <!-- DUMMY PAGINATION STARTS -->
      <div class="row mt-3" v-if="!config.paginationExist && config.showDummy">
        <div class="col-12 d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center flex-wrap">
            <p class="mt-2 mb-2" style="color: #a3a4a6; margin-right: 20px; margin-bottom: 0;">
              Showing 1-{{ tableData.length }} of {{ tableData.length }} entries
            </p>
            <nav class="pagination-minimal">
              <ul class="pagination" style="margin: 0; flex-wrap: wrap;">
                <li class="page-item disabled">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&laquo;</a>
                </li>
                <li class="page-item disabled">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&lsaquo;</a>
                </li>
                <li class="page-item pageNumber" style="cursor: pointer">
                  <a class="page-link" href="javascript:void(0)" style="color: #4B4B4B">
                    <span style="font-weight: bold !important">1</span> of 999
                  </a>
                </li>
                <li class="page-item">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&rsaquo;</a>
                </li>
                <li class="page-item">
                  <a class="page-link" href="javascript:void(0)" style="color: #CBCBCB">&raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      <!-- DUMMY PAGINATION ENDS -->
    </div>
  </div>
</template>

<script>
import { ObserveVisibility } from "vue-observe-visibility";
import moment from "moment";
import XLSX from "xlsx";
import { FoldSpinner } from "vue-spinners";
import "vue-spinners/dist/vue-spinners.css";
import axios from "axios";

export default {
  data() {
    return {
      user: this.$store.getters.currentUser,
      moment,
      mobile: this.isMobile(),
      tableData: [],
      tableHeadersFetched: false,
      customButtons: {
        title: [],
        variable: [],
        class: [],
        style: [],
        FAClass: [],
        condition: [],
      },
      specialButtons: [],
      config: {
        fieldPropsExist: false, //IS 'field' GIVEN IN PROPS
        paginationExist: true, //IS 'pagination' APPLIED
        infiniteScrollEnabled: false, //IS 'infinite_scroll' activated
        substituteData: false, //IS 'api_url' being substituted with 'data'
        disablePerPage: false, //Disables and removes limit per page options on the top left
        showDummy: false, //Shows dummy pagination
        indexing: false, //Show number or index
        fixedHeader: false, //Fixed position header
        tbodyHeight: "500px", //tbody height
        rowStyleCondition: [],
        hide_column: [],

        //Data Configs
        processData: null, //Process data after api is called through an executable string
        dataPath: "resp.data.data", //Path from resp to data
        metaPath: "resp.data.meta", //Path from resp to meta

        //Pagination Configs
        disableLimitPerPageFetch: false,
        limitPerPageOptions: [15, 30, 50],
        currentlyFetching: false,

        exportFile: {
          name: "Data Download",
          type: "xlsx",
          all: false,
        },

        //PAGINATION CONFIGS
        currentPage: 1,
        maxPage: 1,
        total: 0,
        limitPerPage: 15, //LIMIT PER PAGE
        by: null,
        order: false, //true = ascending, false = descending
        tableStriped: true,
        theadBackgroundColor: "#FFF",
      },
      tableHeaders: {
        displayed: [],
        originalTitles: [],
      },
      filterUrl: "",
      propsValid: {
        valid: true,
        reason: "",
      },
      exportData: [],
    };
  },
  props: [
    // Data sources
    "data", //DATA IN OBJECT FORM - Array of JSON Object (ex.[{ 'name' : 'Andre', 'phone' : '12345' },{ 'name' : 'Bob', 'phone' : '23145' }])
    "api_url", //[REQUIRED IF NO DATA] URL OF API TO CALL - String (ex. '/api/callme')
    "update", //CALL API & UPDATE TABLE - Integer (ex. 1,2,3,...)
    "process_data", //PROCESS DATA AFTER API IS CALLED, EVAL() IS USED - String (ex. 'data = data.map((x) => return {id:x.id, name:x.customer.name})')
    "filter", //FILTER - JSON Object (ex. { 'name' : 'Andre', 'phone' : '12345' })
    "path_to_data", //THE PATH TO DATA FROM RESP; DEFAULT 'resp.data.data' String (ex: 'resp.data')
    "path_to_meta", //THE PATH TO META FROM RESP; DEFAULT 'resp.data.meta' String (ex: 'resp.meta')

    // Table Layout & Features
    "fields", //FIELDS NAME AND VARIABLE INCLUDING ORDER OF FIELDS - Array of Objects (ex. [{'title' : 'Name', 'variable' : 'name'},{'title' : 'Created At', 'variable' : 'created_at'}])
    "hide_column", //HIDE UNWANTED COLUMNS BY VARIABLE; Array of String('Column Variable Key') (ex. ['id', 'updated_at', 'deleted_at'])
    "indexing", //ADD INDEX OR NUMBERING SYSTEM ON THE TABLE; DEFAULT FALSE - Boolean (ex. true, false)
    "fixed_header", //FIXED TITLE HEADERS; DEFAULT false - Boolean (ex. true, false)
    "tbody_height", //MAX HEIGHT FOR TBODY; DEFAULT 500px - String (ex. 200px, 10em)
    "table_striped", //Choose if table want to be Striped or not; DEFAULT true - Boolean (ex. true, false),
    "thead_background_color", // Set Background Color of Thead; DEFAULT #FFF - String Hex Code Color (ex. #D8E8ED , #D8E8EF)
    "row_style_condition", //STYLE CONDITION FOR THE WHOLE ROW; DEFAULT null - Object (ex. {'highlightRow == true' : 'background-color: yellow'})

    // Sort & Filter
    "sort_by", //DEFAULT SORT BY VARIABLE - String (ex. 'created_at')
    "sort_order", //DEFAULT SORT ORDER - String (ex. ASC, DESC)
    "disable_sort", //DISABLE SORT FOR ALL FIELDS - Boolean (ex. true, false)

    // Pagination & Scrolling
    "pagination", //IS PAGINATION ENABLED; DEFAULT TRUE - Boolean (ex. true, false)
    "limit_per_page", //LIMIT PER PAGE - Integer (ex. 15, 30, 50,...)
    "limit_per_page_options", //LIMIT PER PAGE OPTIONS - Array of Integers (ex. [15, 30, 50,...])
    "disable_limit_per_page", //DISABLE AND REMOVE PER PAGE OPTIONS - Boolean (ex. true, false)
    "infinite_scroll", //IS THE TABLE AN INFINITE SCROLL TYPE; DEFAULT FALSE - Boolean (ex. true, false)
    "dummy_table", //REVEAL NON-FUNCTIONING PAGINATION - Boolean (ex. true, false)

    // Buttons & Actions
    "buttons", //WHAT KIND OF BUTTONS ON THE END OF EACH ROW - Array of String or Array of Objects (ex. ['Edit', 'Delete'])
    "special_buttons", //BUTTONS PLACED ON THE TOP RIGHT OF THE TABLE - Array of String (ex. ['newUser', 'newBranch'])

    // Export
    "export_config", //EXPORT CONFIG; name(ex. 'Data'), type(ex. 'csv', 'xlsx'), all(true, false)
  ],

  /*
  =============== THE CONTENT OF 'fields': ===============
  > 'title' [REQUIRED] = The displayed title of the field
  > 'variable' [REQUIRED] = The variable that field will take the data from
  > 'class' = CSS class that will be inserted to the 'td' element of the data
  > 'disable_sort' = disable sorting the whole field
  > 'class_condition' = If value inside the data of field matches with the ones in condition, it will show as the class in the 'td' element
  > 'style_condition' = If value inside the data of field matches with the ones in condition, it will show as the style in the 'td' element
  > 'number_formatting' = true/false, whether the data will be formatted using thousands seperator (ex. 10,000)
  > 'date_format' = Use the moment js date format input as string
  > 'is_image' = true/false, whether it is an image url that should be displayed as image or not
  > 'emit_full' = true/false, emits the full data when on${Variable} click, if not, it will just emit what's the content inside it

  Format of 'class_condition' and 'style_condition':
  [
    {'value' : 'class/style'},
    {'value' : 'class/style'},
    {'value' : 'class/style'}
  ]
  //Explanation:
  //'value' indicates the exact value that it must contain inorder to activate the 'class' or 'style' condition
  //'class/style', whether it is used in 'class_condition' or 'style_condition', it is the 'class' or 'style' that will be inserted into the 'td' element



  =============== THE CONTENT OF 'buttons': ===============
  > 'title' [REQUIRED] = The displayed title of the button and the return of the variable it is emitted from, on format of 'on${Variable}' (ex. title='lowerCase' then the emitted='onLowerCase')
  > 'variable' [OPTIONAL] = The variable that the emitted data would be, if not given, it will emit the whole row of data and must be run through 'JSON.parse()' as it has been stringified
  > 'class' = Class that will be attached to the button, all bootstrap elements such as 'btn-outline-info' and font awesome icons such as 'far fa-times' can be used here
  > 'style' = Style that will be attached to the button, making it the highest order of style to be prioritized over
  > 'condition' = An executable condition; when this statement is executed, if it returns false, the button will not be displayed. Data will be represented as 'data' object (ex. data.lat != 'N/A' || data.lng != 'N/A')

  */

  components: {
    fold: FoldSpinner,
  },
  directives: {
    ObserveVisibility,
  },
  mounted() {
    this.checkPropsPreAPI();
  },
  watch: {
    update: function () {
      if (this.config.currentlyFetching) return;
      if (this.config.infiniteScrollEnabled) this.tableData.length = 0;
      this.fetchData(1);
    },
    "config.limitPerPage": function () {
      if (!this.config.disableLimitPerPageFetch) this.fetchData(1);
      else this.config.disableLimitPerPageFetch = false;
    },
  },
  methods: {
    $eval(data, variableText) {
      return eval(variableText);
    },
    newError(errorMessage) {
      var error = "%c" + errorMessage;
      console.log(error, "color:#ff0000");
    },
    numberWithCommas(x) {
      return parseInt(x)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    toCapitalizedWords(name) {
      var words = name.match(/[A-Za-z][a-z]*/g) || [];
      return words.map(this.capitalize).join(" ");
    },
    capitalize(s) {
      if (typeof s !== "string") return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    //Check export button and emits other button
    emit(buttonTitle, variableValue) {
      //If button title has the work 'export'
      if (buttonTitle.toLowerCase().match(/(export|EXPORT)/)) {
        if (!(this.export_config && this.export_config.type)) {
          if (buttonTitle.toLowerCase().match(/(xlsx|excel)/))
            this.config.exportFile.type = "xlsx";
          else if (buttonTitle.toLowerCase().match(/(csv)/))
            this.config.exportFile.type = "csv";
        }
        this.exportFile();
      } else {
        if (variableValue != null)
          eval(
            `this.$emit('on${this.capitalize(buttonTitle)}', '${JSON.stringify(
              variableValue
            )}')`
          );
        else eval(`this.$emit('on${this.capitalize(buttonTitle)}')`);
        // console.log(`this.$emit('on${this.capitalize(buttonTitle)}', '${variableValue}')`);
      }
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
        ? true
        : false;
    },
    determineCondition(mode, condition, value) {
      if (typeof condition == "object" && condition != null) {
        for (var i = 0; i < condition.length; i++) {
          if (condition[i].value == value) {
            return condition[i][mode];
          }
        }
      }
      return "";
    },
    checkPropsPreAPI() {
      //CHECK api_url or data [REQUIRED EITHER api_url OR data]
      if (this.api_url == null || this.api_url == "" || this.api_url == "NaN") {
        if (this.data == null) {
          this.propsValid.valid = false;
          this.propsValid.reason = "'api_url' is Empty!";
        } else {
          //'api_url' is being substituted with 'data'
          this.config.substituteData = true;
          this.config.disablePerPage = true; //auto true on disable per page when substituted
          this.config.paginationExist = false; //auto false on pagination when substituted
        }
      } else {
        if (this.data != null && this.data.length != 0) {
          //if both api_url and data has been filled in
          this.propsValid.valid = false;
          this.propsValid.reason =
            "Choose either 'api_url' or 'data' to be filled in, cannot be both!";
        }
      }

      //CHECK sort_by
      if (this.sort_by == null || this.sort_by == "" || this.sort_by == "NaN") {
      } else {
        this.config.by = this.sort_by;
      }

      //CHECK limit_per_page_options
      if (this.limit_per_page_options != null) {
        if (!Array.isArray(this.limit_per_page_options)) {
          this.propsValid.valid = false;
          this.propsValid.reason = "limit_per_page_options must be an array!";
        } else {
          for (var i = 0; i < this.limit_per_page_options.length; i++) {
            if (typeof this.limit_per_page_options[i] != "number") {
              this.propsValid.valid = false;
              this.propsValid.reason =
                "limit_per_page_options must be an array of integers!";
            }
          }
          this.config.limitPerPageOptions.length = 0;
          this.config.limitPerPageOptions = this.limit_per_page_options;
          this.config.limitPerPage = this.limit_per_page_options[0];
        }
      }

      //CHECK limit_per_page
      if (this.limit_per_page != null) {
        if (
          this.limit_per_page_options == null ||
          this.config.limitPerPageOptions.find(
            (opt) => opt == this.limit_per_page
          )
        ) {
          if (typeof this.limit_per_page != "number") {
            this.propsValid.valid = false;
            this.propsValid.reason = "limit_per_page must be an integer!";
          } else {
            this.config.limitPerPage = this.limit_per_page;
            this.config.disableLimitPerPageFetch = true;
          }
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "limit_per_page cannot be found inside limit_per_page_options";
        }
      }

      //CHECK disable_limit_per_page
      if (this.disable_limit_per_page != null) {
        if (typeof this.disable_limit_per_page == "boolean") {
          this.config.disablePerPage = this.disable_limit_per_page;
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "disable_limit_per_page must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK sort_order
      if (this.sort_order != null) {
        if (
          this.sort_order.toLowerCase() === "asc" ||
          this.sort_order.toLowerCase() === "desc"
        ) {
          this.sort_order.toLowerCase() === "asc"
            ? (this.config.order = true)
            : (this.config.order = false);
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "'sort_order' can only have a value of either 'ASC' or 'DESC'";
        }
      }

      //CHECK table_striped
      if (this.table_striped != null) {
        if (typeof this.table_striped == "boolean") {
          this.config.tableStriped = this.table_striped;
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "table_striped must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK thead_background_color
      if (this.thead_background_color != null) {
        if (typeof this.thead_background_color == "string") {
          this.config.theadBackgroundColor = this.thead_background_color;
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "table_striped must be a string of hex code color";
        }
      }

      //CHECK pagination
      if (this.pagination != null) {
        if (typeof this.pagination == "boolean") {
          this.config.paginationExist = this.pagination;
          if (
            !(
              this.disable_limit_per_page != null &&
              typeof this.disable_limit_per_page == "boolean"
            )
          )
            this.config.disablePerPage = !this.pagination;
        } else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "Pagination must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK buttons
      if (this.buttons != null) {
        if (!Array.isArray(this.buttons)) {
          this.propsValid.valid = false;
          this.propsValid.reason = "Buttons must be an array of string!";
        } else if (this.buttons.length < 1) {
          this.propsValid.valid = false;
          this.propsValid.reason = "Buttons is an empty array";
        } else {
          if (typeof this.buttons[0] == "string") {
            for (var i = 0; i < this.buttons.length; i++) {
              if (this.buttons[i] == null || this.buttons[i] == "") {
                this.propsValid = false;
                this.propsValid.reason = "Buttons cannot have an empty string";
              }
            }
          } else {
            var customButtonTitle = new Array(this.buttons.length).fill(0);
            var customButtonVariable = new Array(this.buttons.length).fill(0);
            var customButtonClass = new Array(this.buttons.length).fill(0);
            var customButtonStyle = new Array(this.buttons.length).fill(0);
            var customButtonFAClass = new Array(this.buttons.length).fill(0);
            var customButtonCondition = new Array(this.buttons.length).fill(
              true
            );

            for (var i = 0; i < this.buttons.length; i++) {
              //Check 'title' [REEQUIRED]
              if (this.buttons[i].title == null) {
                //TITLES
                this.propsValid = false;
                this.propsValid.reason = "'buttons' props requires 'title'";
              } else {
                customButtonTitle[i] = this.buttons[i].title;
              }

              //Check 'variable' [REQUIRED]
              if (
                this.buttons[i].variable != null &&
                this.buttons[i].variable != ""
              ) {
                customButtonVariable[i] = this.buttons[i].variable;
              }

              //Check 'class' and font awesome class inside it
              if (this.buttons[i].class != null) {
                var compiledClass = [[], []]; //[0]FAClass [1]Class
                for (
                  var j = 0;
                  j < this.buttons[i].class.split(/[\s]+/).length;
                  j++
                ) {
                  if (this.buttons[i].class.split(/[\s]+/)[j].includes("fa")) {
                    compiledClass[0].push(
                      this.buttons[i].class.split(/[\s]+/)[j]
                    );
                  } else {
                    compiledClass[1].push(
                      this.buttons[i].class.split(/[\s]+/)[j]
                    );
                  }
                }

                if (compiledClass[0].length != 0) {
                  //FAClass
                  var stringFAClass = "";
                  for (var k = 0; k < compiledClass[0].length; k++) {
                    stringFAClass += `${compiledClass[0][k]}${
                      k != compiledClass[0].length - 1 ? " " : ""
                    }`;
                  }
                  customButtonFAClass[i] = stringFAClass;
                }
                if (compiledClass[1].length != 0) {
                  //Class
                  var stringClass = "";
                  for (var k = 0; k < compiledClass[1].length; k++) {
                    stringClass += `${compiledClass[1][k]}${
                      k != compiledClass[1].length - 1 ? " " : ""
                    }`;
                  }
                  customButtonClass[i] = stringClass;
                }
              }

              //Check 'style'
              if (this.buttons[i].style != null) {
                customButtonStyle[i] = this.buttons[i].style;
              }

              //Check 'condition'
              if (this.buttons[i].condition != null) {
                customButtonCondition[i] = this.buttons[i].condition;
              }
            }
            this.customButtons.title = customButtonTitle;
            this.customButtons.variable = customButtonVariable;
            this.customButtons.class = customButtonClass;
            this.customButtons.style = customButtonStyle;
            this.customButtons.FAClass = customButtonFAClass;
            this.customButtons.condition = customButtonCondition;
          }
        }
      }

      //CHECK special_buttons
      if (this.special_buttons != null) {
        if (!Array.isArray(this.special_buttons)) {
          this.propsValid.valid = false;
          this.propsValid.reason = "special_buttons must be an array of String";
        } else {
          for (var i = 0; i < this.special_buttons.length; i++) {
            if (typeof this.special_buttons[i] == "string") {
              this.specialButtons.push({
                title: this.toCapitalizedWords(this.special_buttons[i]),
                variable: this.special_buttons[i],
              });
            } else {
              this.propsValid.valid = false;
              this.propsValid.reason =
                "special_buttons must be an array of String";
            }
          }
        }
      }

      //CHECK fields
      if (this.fields != null) {
        this.config.fieldPropsExist = true;
        for (var i = 0; i < this.fields.length; i++) {
          if (this.fields[i].title == null || this.fields[i].variable == null) {
            this.propsValid.valid = false;
            this.propsValid.reason =
              "Every field must have a 'title' and a 'variable'";
            this.newError(JSON.stringify(this.fields[i]));
          }
        }
      }

      //CHECK disable_sort
      if (this.disable_sort != null) {
        if (typeof this.disable_sort != "boolean") {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "disable_sort must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK infinite_scroll
      if (this.infinite_scroll != null) {
        if (typeof this.infinite_scroll != "boolean") {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "infinite_scroll must be either a 'true' or a 'false' Boolean";
        } else if (this.infinite_scroll && this.pagination) {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "'infinite_scroll' contradicts with 'pagination' prop";
        } else {
          this.config.infiniteScrollEnabled = this.infinite_scroll;
        }
      }

      //CHECK dummy_table
      if (this.dummy_table != null) {
        if (typeof this.dummy_table == "boolean")
          this.config.showDummy = this.dummy_table;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "dummy_table must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK indexing
      if (this.indexing != null) {
        if (typeof this.indexing == "boolean")
          this.config.indexing = this.indexing;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "indexing must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK process_data
      if (this.process_data != null) {
        if (typeof this.process_data == "string")
          this.config.processData = this.process_data;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason = "process_data must be an executable string";
        }
      }

      //CHECK fixed_header
      if (this.fixed_header != null) {
        if (typeof this.fixed_header == "boolean")
          this.config.fixedHeader = this.fixed_header;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "fixed_header must be either a 'true' or a 'false' Boolean";
        }
      }

      //CHECK tbody_height
      if (this.tbody_height != null) {
        if (this.tbody_height.match(/[\d]+(%|em|px)/))
          this.config.tbodyHeight = this.tbody_height;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason =
            "tbody_height must be either an 'em', 'px', or '%' height";
        }
      }

      //CHECK export_config
      if (this.export_config != null) {
        if (this.export_config.name)
          this.config.exportFile.name = this.export_config.name;
        if (this.export_config.type)
          this.config.exportFile.type = this.export_config.type;
        if (this.export_config.all)
          this.config.exportFile.all = this.export_config.all;
      }

      //CHECK row_style_condition
      if (this.row_style_condition != null) {
        if (typeof this.row_style_condition != "object") {
          this.propsValid.valid = false;
          this.propsValid.reason = `'row_style_condition' must be an object. Example: {'highlightRow == true' : 'background-color: yellow', 'highlightRow == true' : 'background-color: red'} `;
        } else {
          var conditions = [];
          for (var i in this.row_style_condition) {
            conditions.push({
              condition: i,
              style: this.row_style_condition[i],
            });
          }
          this.config.rowStyleCondition = conditions;
        }
      }

      //CHECK hide_column
      if (this.hide_column != null) {
        if (!Array.isArray(this.hide_column)) {
          this.propsValid.valid = false;
          this.propsValid.reason = "hide_column must be an array of String";
        } else {
          this.config.hide_column = [];
          for (var i = 0; i < this.hide_column.length; i++) {
            if (typeof this.hide_column[i] == "string")
              this.config.hide_column.push(this.hide_column[i]);
            else {
              this.propsValid.valid = false;
              this.propsValid.reason = "hide_column must be an array of String";
            }
          }
        }
      }

      //Check path_to_data
      if (this.path_to_data != null) {
        if (typeof this.path_to_data == "string")
          this.config.dataPath = this.path_to_data;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason = "path_to_data must be a string";
        }
      }

      //Check path_to_meta
      if (this.path_to_meta != null) {
        if (typeof this.path_to_meta == "string")
          this.config.metaPath = this.path_to_meta;
        else {
          this.propsValid.valid = false;
          this.propsValid.reason = "path_to_meta must be a string";
        }
      }

      //IF EVERYTHING IS VALID, CONTINUE TO FETCH API
      if (this.propsValid.valid) this.fetchData(1);
    },
    async checkPropsPostAPI() {
      //IS THIS FIRST TIME FETCH API?
      if (!this.tableHeadersFetched) {
        //CHECK buttons
        if (this.tableData.length > 0 && this.buttons != null) {
          for (var i = 0; i < this.buttons.length; i++) {
            if (
              this.buttons[i].variable != null &&
              this.buttons[i].variable != "" &&
              typeof this.tableData[0][this.buttons[i].variable] == "undefined"
            ) {
              this.propsValid.valid = false;
              this.propsValid.reason = `Button '${this.buttons[i].title}' of variable '${this.buttons[i].variable}' was not sent from the API Response`;
            }
          }
        }

        //CHECK fields
        if (this.config.fieldPropsExist) {
          var fieldsValid = true;

          var titles = [];
          var variables = [];
          var classes = new Array(this.fields.length).fill(null);
          var disableSort = new Array(this.fields.length).fill(
            this.config.paginationExist
              ? this.disable_sort != null
                ? this.disable_sort
                : false
              : true
          );
          var class_condition = new Array(this.fields.length).fill(null);
          var style_condition = new Array(this.fields.length).fill(null);
          var numberWithCommas = new Array(this.fields.length).fill(false);
          var dateFormat = new Array(this.fields.length).fill(null);
          var isImage = new Array(this.fields.length).fill(false);
          var emitFull = new Array(this.fields.length).fill(false);

          //Loop Check Starts
          for (var i = 0; i < this.fields.length; i++) {
            //Check if 'variable' exist in API Response
            if (
              this.tableData.length > 0 &&
              typeof this.tableData[0][this.fields[i].variable] == "undefined"
            ) {
              this.propsValid.valid = false;
              this.propsValid.reason = `Field variable of '${this.fields[i].variable}' was not sent from the API Response`;
              return;
            }

            //Check 'class'
            if (typeof this.fields[i].class != "undefined") {
              if (typeof this.fields[i].class != "string") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'class' request`;
                return;
              } else {
                classes[i] = this.fields[i].class;
              }
            }

            //Check 'disable_sort'
            if (typeof this.fields[i].disable_sort != "undefined") {
              if (typeof this.fields[i].disable_sort != "boolean") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'disable_sort' request`;
                return;
              } else {
                disableSort[i] = this.fields[i].disable_sort;
              }
            }

            //Check 'class_condition'
            if (typeof this.fields[i].class_condition != "undefined") {
              if (typeof this.fields[i].class_condition != "object") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'class_condition' request`;
              } else {
                var conditions = [];
                var class_keys = Object.keys(this.fields[i].class_condition);
                for (var j = 0; j < class_keys.length; j++) {
                  conditions.push({
                    value: class_keys[j],
                    class: this.fields[i].class_condition[class_keys[j]],
                  });
                }
                class_condition[i] = conditions;
              }
            }

            //Check 'style_condition'
            if (typeof this.fields[i].style_condition != "undefined") {
              if (typeof this.fields[i].style_condition != "object") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'style_condition' request`;
              } else {
                var conditions = [];
                var style_keys = Object.keys(this.fields[i].style_condition);
                for (var j = 0; j < style_keys.length; j++) {
                  conditions.push({
                    value: style_keys[j],
                    style: this.fields[i].style_condition[style_keys[j]],
                  });
                }
                style_condition[i] = conditions;
              }
            }

            //Check 'number_formatting'
            if (typeof this.fields[i].number_formatting != "undefined") {
              if (typeof this.fields[i].number_formatting != "boolean") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'number_formatting' request`;
                return;
              } else {
                numberWithCommas[i] = this.fields[i].number_formatting;
              }
            }

            //Check 'date_format'
            if (typeof this.fields[i].date_format != "undefined") {
              if (typeof this.fields[i].date_format != "string") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'date_format' request`;
                return;
              } else {
                dateFormat[i] = this.fields[i].date_format;
              }
            }

            //Check 'is_image'
            if (typeof this.fields[i].is_image != "undefined") {
              if (typeof this.fields[i].is_image != "boolean") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'is_image' request`;
                return;
              } else {
                isImage[i] = this.fields[i].is_image;
              }
            }

            //Check 'emit_full'
            if (typeof this.fields[i].emit_full != "undefined") {
              if (typeof this.fields[i].emit_full != "boolean") {
                this.propsValid.valid = false;
                this.propsValid.reason = `Field variable of '${this.fields[i].variable}' has an invalid 'emit_full' request`;
                return;
              } else {
                emitFull[i] = this.fields[i].emit_full;
              }
            }

            titles.push(this.fields[i].title);
            variables.push(this.fields[i].variable);
          }
          //Loop Check Ends

          this.tableHeaders.originalTitles = titles;
          var compiledDisplay = [];

          for (var i = 0; i < variables.length; i++) {
            compiledDisplay.push({
              title: titles[i],
              variable: variables[i],
              class: classes[i],
              disableSort: disableSort[i],
              class_condition: class_condition[i],
              style_condition: style_condition[i],
              numberWithCommas: numberWithCommas[i],
              dateFormat: dateFormat[i],
              isImage: isImage[i],
              emitFull: emitFull[i],
            });
          }
          this.tableHeaders.displayed = compiledDisplay;
        } else {
          await this.processTableHeaders();
        }
      }
      this.changeSortTitle();
    },
    initialiseFilter() {
      if (!(typeof this.filter === "object" && this.filter !== null)) {
        this.filterUrl = "";
        return;
      }
      var keys = Object.keys(this.filter);
      let stringUrl = "";
      var queryParams = this.filter;

      for (var i = 0; i < keys.length; i++) {
        if (!queryParams[keys[i]]) continue;

        if (queryParams[keys[i]] == "" || queryParams[keys[i]].length === 0)
          continue;

        // if((keys[i] == 'user_type') && queryParams[keys[i]] == 'All')
        //   continue;

        // if(keys[i] == 'start_hour' || keys[i] == 'end_hour')
        //   queryParams[keys[i]] += ":00";

        // if([keys[i]] == 'start' || keys[i] == 'end') queryParams[keys[i]] = moment(queryParams[keys[i]]).format('Y-MM-DD')

        stringUrl += `&${keys[i]}=${queryParams[keys[i]]}`;
      }
      this.filterUrl = stringUrl;
    },
    async fetchData(page, isExport) {
      if (this.config.substituteData) {
        //IF 'api_url' has been substituted with data
        let data = this.data;
        if (this.config.processData != null) eval(this.config.processData);

        this.tableData = data;
        this.checkPropsPostAPI();
        return;
      }

      this.config.currentlyFetching = true;
      this.$emit("fetchStart");

      let vm = this;
      await this.initialiseFilter();
      let seperator = this.api_url.includes("?") ? "&" : "?";

      var url = `${this.api_url}${seperator}pagination=${
        this.config.paginationExist ? "true" : "false"
      }${this.filterUrl}`;

      // var url = `https://sahmoil-sso.ahm.to${this.api_url}`;

      if (this.config.paginationExist) {
        url = `${url}&limit=${this.config.limitPerPage}${
          this.config.by != null ? "&order=" : ""
        }${this.config.by != null ? (this.config.order ? "ASC" : "DESC") : ""}${
          this.config.by != null ? "&by=" + this.config.by : ""
        }`; //ADDS LIMIT, ORDER, AND BY

        if (page) url = url + "&page=" + page;
        else url = url + "&page=1";
      }

      const token = this.user.token;
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const resp = await this.$httpSSO.get("/api/admin/user?page=1&limit=150", {
        headers,
      });
      const total = resp.data.data.length;
      // console.log("total data:", total);

      // await axios
      await this.$httpSSO
        .get(url, { headers })
        .then((resp) => {
          var data = eval(this.config.dataPath);
          console.log("fetch user data from sso server:", data);

          if (Array.isArray(data)) {
            data = data.map((item) => ({
              ...item,
              status: item.status === 1 ? "ACTIVE" : "INACTIVE",
              product_category: item.product_category.join(", "),
            }));
          }

          //process data after api is called, if process_data props is sent
          if (vm.config.processData != null) eval(vm.config.processData);

          if (isExport) {
            this.exportData = data;
          } else {
            if (vm.config.paginationExist) {
              var meta = eval(this.config.metaPath);

              console.log("meta page:", typeof Number(meta.page));

              vm.config.currentPage =
                Number(meta.page) != null
                  ? Number(meta.page)
                  : meta.current_page;
              vm.config.maxPage =
                meta.total_page != null
                  ? meta.total_page
                  : meta.last_page != null
                  ? meta.last_page
                  : data.length < this.config.limitPerPage
                  ? 0
                  : -1; //-1 Continue Infinite Scroll, 0 Stop Infinite Scroll

              // console.log("data path:", eval(this.config.dataPath));

              // vm.config.total = meta.total;
              vm.config.total = total;
            }

            vm.tableData = vm.config.infiniteScrollEnabled
              ? vm.tableData.concat(data)
              : data;
          }
        })
        .then(() => {
          vm.checkPropsPostAPI();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          vm.config.currentlyFetching = false;
          this.$emit("fetchEnd");
        });
      return "Export Complete";
    },
    async processTableHeaders() {
      // console.log(`${JSON.stringify(this.tableData)}; ${Array.isArray(this.tableData)}`);
      if (!Array.isArray(this.tableData)) {
        this.newError("Data is not array!");
        return;
      } else if (this.tableData.length == 0) {
        this.newError("Data is empty!");
        return;
      }

      var titles = [];
      var keys = Object.keys(this.tableData[0]);

      for (var i = 0; i < keys.length; i++) {
        if (keys[i].includes("_")) {
          var str = await this.capitalize(keys[i].split("_")[0]);
          for (var j = 1; j < keys[i].split("_").length; j++) {
            str += ` ${await this.capitalize(keys[i].split("_")[j])}`;
          }
          titles.push(str);
        } else {
          titles.push(await this.capitalize(keys[i]));
        }
      }

      this.tableHeaders.originalTitles = titles;

      var compiledDisplay = [];

      for (var i = 0; i < keys.length; i++) {
        compiledDisplay.push({
          title: titles[i],
          variable: keys[i],
          disableSort: this.config.paginationExist
            ? this.disableSort != null
              ? this.disableSort
              : false
            : true, //FORCE DISABLE SORT IF NO PAGINATION
        });
      }
      this.tableHeaders.displayed.length = 0;
      this.tableHeaders.displayed =
        this.tableHeaders.displayed.concat(compiledDisplay);
      // console.log(JSON.stringify(this.tableHeaders));

      this.tableHeadersFetched = true;
    },
    selectAndSort(sortVariable, disableSort) {
      if (sortVariable == null || disableSort) {
        this.newError("Sort Unavailable");
        return;
      }

      if (this.config.infiniteScrollEnabled) this.tableData.length = 0;

      if (this.config.by != sortVariable) {
        this.config.by = sortVariable;
        this.config.order = false;
        this.fetchData(1);
      } else {
        this.config.order = !this.config.order;
        if (this.config.order) this.fetchData(1);
        else this.fetchData(1);
      }
    },
    changeSortTitle() {
      for (var i = 0; i < this.tableHeaders.displayed.length; i++) {
        // console.log(`${this.tableHeaders.displayed[i].variable} == ${this.config.by}`)
        if (this.config.by == this.tableHeaders.displayed[i].variable) {
          if (this.config.order)
            this.tableHeaders.displayed[i].title =
              this.tableHeaders.originalTitles[i] + "▲";
          else
            this.tableHeaders.displayed[i].title =
              this.tableHeaders.originalTitles[i] + "▼";
        } else {
          this.tableHeaders.displayed[i].title =
            this.tableHeaders.originalTitles[i];
        }
      }
    },
    changePage(opt) {
      //1 First, 5 previous, 6 next, 10 last
      switch (opt) {
        case 1: //laquo - First Page
          if (this.config.currentPage - 1 > 0) {
            this.fetchData(1);
          } else {
            this.newError("First Page Reached!");
          }
          break;
        case 5: //lsaquo - Previous Page
          if (this.config.currentPage - 1 > 0) {
            this.fetchData(this.config.currentPage - 1);
          } else {
            this.newError("First Page Reached!");
          }
          break;
        case 6: //rsaquo - Next Page
          if (this.config.currentPage + 1 <= this.config.maxPage) {
            this.fetchData(this.config.currentPage + 1);
          } else {
            this.newError("Last Page Reached!");
          }
          break;
        case 10: //raquo - Last Page
          if (this.config.currentPage + 1 <= this.config.maxPage) {
            this.fetchData(this.config.maxPage);
          } else {
            this.newError("Last Page Reached!");
          }
          break;
        default:
          this.newError("Pagination Error, Read Documentation");
      }
    },
    loadingSeen(isVisible, entry) {
      if (
        isVisible &&
        (this.config.currentPage < this.config.maxPage ||
          this.config.maxPage == -1) &&
        !this.config.currentlyFetching
      )
        this.fetchData(this.config.currentPage + 1);
    },
    async exportFile() {
      if (this.config.exportFile.all) {
        let message = "Export Failed";

        this.config.paginationExist = false;
        message = await this.fetchData(1, true);
        this.config.paginationExist = true;
        message += "!";
        console.log(message);
      }

      let wb = XLSX.utils.book_new();
      var ws = XLSX.utils.json_to_sheet(this.exportData, { raw: true });
      XLSX.utils.book_append_sheet(wb, ws, "Data");

      let exportFileName = `${this.config.exportFile.name}.${this.config.exportFile.type}`;
      XLSX.writeFile(wb, exportFileName);
    },
    processRowStyle(singleRow) {
      let styles = "";
      for (var i in this.config.rowStyleCondition) {
        if (eval("singleRow." + this.config.rowStyleCondition[i].condition))
          styles += this.config.rowStyleCondition[i].style + ";";
      }
      return styles;
    },
  },
};
</script>

<style scoped>
.outerBox {
  padding: 0 20px;
}

.innerBox {
  border: 1px solid #eeeeee;
  border-radius: 5px;
  background-color: white;
  /* box-shadow: 10px 10px 20px #E0E0E0; */
}

.tableTitle {
  text-align: left;
}

.sortable {
  cursor: pointer;
}

.tableContent {
  text-align: left;
  vertical-align: middle;
}

/* PAGINATION STYLE STARTS */
.page-item {
  background: transparent !important;
}

.page-link {
  cursor: pointer;
  background: transparent !important;
  border: none !important;
}

.pageNumber .page-link:hover {
  background: transparent !important;
  cursor: default;
}

.disabled .page-link {
  background: transparent !important;
}

li .disabled:hover {
  cursor: default;
}

/* PAGINATION STYLE ENDS */

.selectPerPage:active,
.selectPerPage:hover {
  outline: none;
}

.specialButtons {
  display: flex;
  justify-content: space-between;
}

thead tr th {
  vertical-align: middle;
}

/* FIXED HEADER START */
.fixedHeader thead,
.fixedHeader tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.fixedHeader thead {
  width: calc(100% - 0.7em);
}

.fixedHeader tbody {
  display: block;
  /* max-height:200px; */
  overflow-y: auto;
}

.fixedHeader tbody::-webkit-scrollbar {
  width: 0.7em;
}

.fixedHeader tbody::-webkit-scrollbar-track {
  background-color: #f9f9f9;
  border-radius: 20px;
}

.fixedHeader tbody::-webkit-scrollbar-thumb {
  background-color: #a4a4a4;
  border-radius: 20px;
}

/* FIXED HEADER END */

/* Scan History Table Styles */
.scan-history-table-wrapper .scan-history-table {
  border-collapse: separate;
  border-spacing: 0;
  border: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody tr {
  background-color: #FFFFFF !important;
  border-top: none !important;
  border-bottom: none !important;
}

.scan-history-table-wrapper .scan-history-table.table-striped tbody tr {
  background-color: #FFFFFF !important;
  border-top: none !important;
  border-bottom: none !important;
}

.scan-history-table-wrapper .scan-history-table.table-striped tbody tr:nth-of-type(odd) {
  background-color: #FFFFFF !important;
}

.scan-history-table-wrapper .scan-history-table thead th {
  color: #9D9D9D;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid #dee2e6;
  border-top: none !important;
}

.scan-history-table-wrapper .scan-history-table thead th:first-child {
  border-left: none !important;
}

.scan-history-table-wrapper .scan-history-table thead th:last-child {
  border-right: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody td {
  color: #4B4B4B;
  text-align: left;
  vertical-align: middle;
  background-color: #FFFFFF !important;
  border-top: none !important;
  border-bottom: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody td:first-child {
  border-left: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody td:last-child {
  border-right: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody tr:first-child td {
  border-top: none !important;
}

.scan-history-table-wrapper .scan-history-table tbody tr:last-child td {
  border-bottom: none !important;
}

/* Pagination Styles - Minimalist */
.pagination-minimal .pagination {
  margin-bottom: 0;
}

.pagination-minimal .page-item {
  margin: 0 2px;
}

.pagination-minimal .page-item .page-link {
  background: transparent !important;
  border: none !important;
  color: #4B4B4B;
  padding: 4px 8px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  text-decoration: none;
}

.pagination-minimal .page-item .page-link:hover {
  text-decoration: none;
}

.pagination-minimal .page-item:not(.disabled):hover .page-link {
  background: transparent !important;
  color: #007bff;
}

.pagination-minimal .page-item.disabled .page-link {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.4;
  color: #9D9D9D !important;
}

.pagination-minimal .page-item.active .page-link {
  background: transparent !important;
  border: none !important;
  color: #007bff !important;
  font-weight: 600;
}

.pagination-minimal .page-item.active .page-link:hover {
  background: transparent !important;
  color: #007bff !important;
}

/* Scan History Table Wrapper specific styles */
.scan-history-table-wrapper .pagination-minimal .pagination {
  margin-bottom: 0;
}

.scan-history-table-wrapper .pagination-minimal .page-item {
  margin: 0 2px;
}

.scan-history-table-wrapper .pagination-minimal .page-item .page-link {
  background: transparent !important;
  border: none !important;
  color: #4B4B4B;
  padding: 4px 8px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  text-decoration: none;
}

.scan-history-table-wrapper .pagination-minimal .page-item .page-link:hover {
  text-decoration: none;
}

.scan-history-table-wrapper .pagination-minimal .page-item:not(.disabled):hover .page-link {
  background: transparent !important;
  color: #007bff;
}

.scan-history-table-wrapper .pagination-minimal .page-item.disabled .page-link {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.4;
  color: #9D9D9D !important;
}

.scan-history-table-wrapper .pagination-minimal .page-item.active .page-link {
  background: transparent !important;
  border: none !important;
  color: #007bff !important;
  font-weight: 600;
}

.scan-history-table-wrapper .pagination-minimal .page-item.active .page-link:hover {
  background: transparent !important;
  color: #007bff !important;
}
</style>
